import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, getToken } from '../lib/api';
import { useNova } from '../lib/store';
import { Draft, ReportSchedules } from './ReportSchedules';
import { Button, duration, Panel } from './ui';

type Kind = import('./ReportSchedules').ReportKind;
type Cell = string | number | null;
interface Report {
  title: string;
  from: string;
  to: string;
  timezone: string;
  columns: { key: string; label: string }[];
  rows: Record<string, Cell>[];
}
interface Named {
  id: string;
  name: string;
}

export const KINDS: { id: Kind; label: string; group: 'Queues' | 'Agents' | 'Livechat' | 'Email' | 'Quality'; filters: ('queue' | 'agent' | 'team')[] }[] = [
  { id: 'queues', label: 'Queue summary', group: 'Queues', filters: ['queue'] },
  { id: 'abandoned', label: 'Abandoned calls', group: 'Queues', filters: ['queue'] },
  { id: 'hourly', label: 'Hourly volume', group: 'Queues', filters: ['queue'] },
  { id: 'outcomes', label: 'Outcomes', group: 'Queues', filters: ['queue', 'agent', 'team'] },
  { id: 'agents', label: 'Agent performance', group: 'Agents', filters: ['queue', 'agent', 'team'] },
  { id: 'productivity', label: 'Productivity', group: 'Agents', filters: ['agent', 'team'] },
  { id: 'sessions', label: 'Sign-ins and sign-outs', group: 'Agents', filters: ['agent', 'team'] },
  { id: 'handled', label: 'Interactions handled', group: 'Agents', filters: ['queue', 'agent', 'team'] },
  { id: 'chats', label: 'Livechat summary', group: 'Livechat', filters: ['queue'] },
  { id: 'chat_agents', label: 'Livechat by agent', group: 'Livechat', filters: ['queue', 'agent', 'team'] },
  { id: 'emails', label: 'Email summary', group: 'Email', filters: ['queue'] },
  { id: 'email_agents', label: 'Email by agent', group: 'Email', filters: ['queue', 'agent', 'team'] },
  { id: 'qa_agents', label: 'Quality by agent', group: 'Quality', filters: ['queue', 'agent', 'team'] },
  { id: 'qa_questions', label: 'Question breakdown', group: 'Quality', filters: ['queue', 'agent', 'team'] },
  { id: 'qa_reviews', label: 'Review log', group: 'Quality', filters: ['queue', 'agent', 'team'] },
];

const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function preset(id: string): { from: string; to: string } {
  const now = new Date();
  const back = (n: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() - n);
  switch (id) {
    case 'yesterday':
      return { from: day(back(1)), to: day(back(1)) };
    case 'week':
      return { from: day(back(6)), to: day(now) };
    case 'month':
      return { from: day(new Date(now.getFullYear(), now.getMonth(), 1)), to: day(now) };
    default:
      return { from: day(now), to: day(now) };
  }
}
const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Last 7 days' },
  { id: 'month', label: 'This month' },
];

/** Seconds columns read as m:ss, percentages with a % sign; exports keep the raw numbers. */
function show(label: string, v: Cell): string {
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'number' && label.endsWith('(s)')) return duration(v);
  if (typeof v === 'number' && label.includes('(%')) return `${v}%`;
  if (typeof v === 'number') return v.toLocaleString();
  return v;
}
// "Average wait (s)" reads "Average wait" (cells show m:ss); "Abandoned (%)" stays distinct from the count as "Abandoned %"
const header = (label: string) => label.replace(/ \(s\)$/, '').replace(/ \(%\)$/, ' %').replace(/ \(% of signed in\)$/, ' %');

function useList<T>(path: string): T[] {
  const [rows, setRows] = useState<T[]>([]);
  useEffect(() => {
    api<T[]>('GET', path)
      .then(setRows)
      .catch(() => setRows([]));
  }, [path]);
  return rows;
}

/**
 * Standard reports with filters and CSV or Excel export (PRD RP-01, RP-02).
 * In the Reports hub it shows one report, chosen in the hub's menu (`only`).
 */
export function Reports({ only, channel }: { only?: Kind; channel?: 'telegram' | 'whatsapp' } = {}) {
  const [kind, setKind] = useState<Kind>(only ?? 'queues');
  useEffect(() => {
    if (only) setKind(only);
  }, [only]);
  const [range, setRange] = useState({ id: 'today', ...preset('today') });
  const [f, setF] = useState({ queueId: '', agentId: '', teamId: '' });
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState<Draft | null>(null);
  const queues = useList<Named>('/queues');
  const teams = useList<Named>('/teams');
  const agents = useList<Named & { role: string }>('/users').filter((u) => u.role === 'agent' || u.role === 'supervisor');
  const uses = KINDS.find((k) => k.id === kind)!.filters;

  const query = useMemo(() => {
    const q = new URLSearchParams({ from: range.from, to: range.to });
    if (f.queueId && uses.includes('queue')) q.set('queueId', f.queueId);
    if (f.agentId && uses.includes('agent')) q.set('agentId', f.agentId);
    if (f.teamId && uses.includes('team')) q.set('teamId', f.teamId);
    // The chat reports for a messaging app
    if (channel) q.set('channel', channel);
    return q;
  }, [range, f, uses, channel]);

  useEffect(() => {
    let live = true;
    setError(null);
    api<Report>('GET', `/reports/${kind}?${query}`)
      .then((r) => live && setReport(r))
      .catch((err) => live && (setReport(null), setError(err instanceof ApiError ? err.message : 'Could not load the report')));
    return () => {
      live = false;
    };
  }, [kind, query]);

  async function download(format: 'csv' | 'xlsx') {
    const q = new URLSearchParams(query);
    q.set('format', format);
    const res = await fetch(`/api/v1/reports/${kind}?${q}`, { headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
    if (!res.ok) return useNova.getState().toast('Could not download the report', 'error');
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `report.${format}`;
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const select = 'rounded-lg border border-line bg-abyss/50 px-2.5 py-2 text-sm outline-none focus:border-ion disabled:opacity-40';

  return (
    <div className="mx-auto flex max-w-[1280px] flex-col gap-4">
      <Panel className="p-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-lg font-medium">{report?.title ?? (only ? KINDS.find((k) => k.id === only)?.label : 'Reports')}</h1>
            <p className="text-sm text-ink-3">
              Finished calls, by day in {report?.timezone ?? 'your time zone'}. Download any report as a spreadsheet.
            </p>
          </div>
          <div className="flex gap-2">
            <Button tone="quiet" className="px-3 py-2" onClick={() => void download('csv')}>
              Download CSV
            </Button>
            <Button tone="quiet" className="px-3 py-2" onClick={() => void download('xlsx')}>
              Download Excel
            </Button>
            <Button
              tone="quiet"
              className="px-3 py-2"
              onClick={() => {
                const filters: Draft['filters'] = {};
                if (f.queueId && uses.includes('queue')) filters.queueId = f.queueId;
                if (f.agentId && uses.includes('agent')) filters.agentId = f.agentId;
                if (f.teamId && uses.includes('team')) filters.teamId = f.teamId;
                setScheduling({ report: kind, filters });
              }}
            >
              Email this report…
            </Button>
          </div>
        </div>
        <div className={`mb-4 flex flex-col gap-1.5 ${only ? 'hidden' : ''}`} role="tablist" aria-label="Report">
          {(['Queues', 'Agents'] as const).map((group) => (
            <div key={group} className="flex flex-wrap items-center gap-1">
              <span className="w-16 shrink-0 text-xs text-ink-3">{group}</span>
              {KINDS.filter((k) => k.group === group).map((k) => (
                <button
                  key={k.id}
                  type="button"
                  role="tab"
                  aria-selected={kind === k.id}
                  onClick={() => setKind(k.id)}
                  className={`rounded-lg px-3 py-1.5 text-sm transition ${kind === k.id ? 'bg-ion/15 text-ink' : 'text-ink-2 hover:bg-veil/[0.05] hover:text-ink'}`}
                >
                  {k.label}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex overflow-hidden rounded-lg border border-line" role="group" aria-label="Date range">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={range.id === p.id}
                onClick={() => setRange({ id: p.id, ...preset(p.id) })}
                className={`px-3 py-2 text-sm ${range.id === p.id ? 'bg-ion/15 text-ink' : 'text-ink-2 hover:text-ink'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            From
            <input type="date" className={select} value={range.from} max={range.to} onChange={(e) => e.target.value && setRange({ ...range, id: 'custom', from: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            To
            <input type="date" className={select} value={range.to} min={range.from} onChange={(e) => e.target.value && setRange({ ...range, id: 'custom', to: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Queue
            <select className={select} disabled={!uses.includes('queue')} value={f.queueId} onChange={(e) => setF({ ...f, queueId: e.target.value })}>
              <option value="">All queues</option>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Agent
            <select className={select} disabled={!uses.includes('agent')} value={f.agentId} onChange={(e) => setF({ ...f, agentId: e.target.value })}>
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Team
            <select className={select} disabled={!uses.includes('team')} value={f.teamId} onChange={(e) => setF({ ...f, teamId: e.target.value })}>
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Panel>

      {error ? <Panel className="p-5 text-sm text-break">{error}</Panel> : null}
      {report && kind === 'hourly' ? <HourlyChart rows={report.rows} /> : null}
      {report ? <ReportTable report={report} /> : !error ? <Panel className="p-8 text-center text-sm text-ink-3">Loading…</Panel> : null}
      <ReportSchedules
        titles={Object.fromEntries(KINDS.map((k) => [k.id, k.label])) as Record<Kind, string>}
        editing={scheduling}
        onEdit={setScheduling}
        formOnly={Boolean(only)}
      />
    </div>
  );
}

function ReportTable({ report }: { report: Report }) {
  // Number columns line up on the right in the number font; text stays left; dates stay on one line
  const numeric = new Set(report.columns.filter((c) => report.rows.some((r) => typeof r[c.key] === 'number')).map((c) => c.key));
  const dated = new Set(report.columns.filter((c) => report.rows.some((r) => /^\d{4}-\d{2}-\d{2}/.test(String(r[c.key] ?? '')))).map((c) => c.key));
  const align = (key: string) => (numeric.has(key) ? 'tabular text-right' : dated.has(key) ? 'tabular whitespace-nowrap' : '');
  return (
    <Panel className="overflow-x-auto p-0">
      <table className="w-full min-w-[720px] text-left text-sm">
        <caption className="sr-only">{report.title}</caption>
        <thead className="text-ink-3">
          <tr className="border-b border-line">
            {report.columns.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-2.5 font-normal ${numeric.has(c.key) ? 'text-right' : ''}`}>
                {header(c.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((r, ri) => {
            const total = r.queue === 'All queues';
            return (
              <tr key={ri} className={`border-b border-line/60 last:border-0 ${total ? 'bg-veil/[0.03] font-medium' : ''}`}>
                {report.columns.map((c) => (
                  <td key={c.key} className={`px-4 py-2.5 ${align(c.key)}`}>
                    {show(c.label, r[c.key])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {report.rows.length === 0 ? <p className="px-4 py-10 text-center text-sm text-ink-2">No calls in this period.</p> : null}
    </Panel>
  );
}

/** Inbound calls per hour: answered and abandoned stacked, with a hover tooltip per hour. */
function HourlyChart({ rows }: { rows: Record<string, Cell>[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const data = rows.map((r) => ({
    hour: String(r.hour),
    answered: Number(r.answered ?? 0),
    abandoned: Number(r.abandoned ?? 0),
    offered: Number(r.offered ?? 0),
    wait: r.avg_wait as number | null,
  }));
  const peak = Math.max(1, ...data.map((d) => d.answered + d.abandoned));
  const step = peak <= 5 ? 1 : peak <= 10 ? 2 : peak <= 25 ? 5 : peak <= 50 ? 10 : Math.ceil(peak / 50) * 10;
  const top = Math.ceil(peak / step) * step;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const W = 960;
  const H = 240;
  const pad = { l: 36, r: 8, t: 12, b: 26 };
  const band = (W - pad.l - pad.r) / 24;
  const bar = Math.min(24, band * 0.62);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / top);
  const h = (v: number) => (H - pad.t - pad.b) * (v / top);
  const empty = data.every((d) => d.offered === 0);
  const hd = hover === null ? null : data[hover];

  /** A column segment: square at its bottom, 4px rounded top when it is the top of the stack. */
  const seg = (x: number, yTop: number, height: number, rounded: boolean, fill: string) => {
    if (height <= 0) return null;
    const r = rounded ? Math.min(4, height, bar / 2) : 0;
    const d = `M${x},${yTop + height} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + bar - r} Q${x + bar},${yTop} ${x + bar},${yTop + r} V${yTop + height} Z`;
    return <path d={d} fill={fill} />;
  };

  return (
    <Panel className="p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-medium">Inbound calls by hour</h2>
        <div className="flex gap-4 text-xs text-ink-2" aria-label="Legend">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: 'var(--color-chart-1)' }} /> Answered
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: 'var(--color-chart-2)' }} /> Abandoned
          </span>
        </div>
      </div>
      {empty ? (
        <p className="py-10 text-center text-sm text-ink-2">No inbound calls in this period.</p>
      ) : (
        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Answered and abandoned inbound calls for each hour of the day; the table below has the numbers">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--color-line)" strokeWidth="1" />
                <text x={pad.l - 8} y={y(t)} dy="0.32em" textAnchor="end" fill="var(--color-ink-3)" style={{ font: '11px var(--font-mono)' }}>
                  {t}
                </text>
              </g>
            ))}
            {data.map((d, i) => {
              const x = pad.l + i * band + (band - bar) / 2;
              const gap = d.answered && d.abandoned ? 2 : 0;
              const ha = h(d.answered);
              const hb = Math.max(0, h(d.abandoned) - gap);
              return (
                <g key={d.hour}>
                  {seg(x, y(d.answered), ha, !d.abandoned, 'var(--color-chart-1)')}
                  {seg(x, y(d.answered + d.abandoned), hb, true, 'var(--color-chart-2)')}
                  {i % 3 === 0 ? (
                    <text x={x + bar / 2} y={H - 8} textAnchor="middle" fill="var(--color-ink-3)" style={{ font: '11px var(--font-mono)' }}>
                      {d.hour.slice(0, 2)}
                    </text>
                  ) : null}
                  {/* Hit target: the whole hour band, taller than the bar */}
                  <rect
                    x={pad.l + i * band}
                    y={pad.t}
                    width={band}
                    height={H - pad.t - pad.b}
                    fill={hover === i ? 'var(--color-veil)' : 'transparent'}
                    fillOpacity={hover === i ? 0.05 : 0}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(null)}
                  />
                </g>
              );
            })}
            <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} stroke="var(--color-line-strong)" strokeWidth="1" />
          </svg>
          {hd ? (
            <div
              className="pointer-events-none absolute top-2 z-10 min-w-[150px] rounded-lg border border-line-strong bg-hull px-3 py-2 text-xs shadow-lg"
              style={{ left: `${Math.min(80, ((hover! + 0.5) / 24) * 100)}%` }}
              role="status"
            >
              <p className="mb-1 font-medium">
                {hd.hour}–{String(Number(hd.hour.slice(0, 2)) + 1).padStart(2, '0')}:00
              </p>
              <p className="flex items-center gap-1.5 text-ink-2">
                <span className="h-2 w-2 rounded-sm" style={{ background: 'var(--color-chart-1)' }} /> Answered <span className="tabular ml-auto text-ink">{hd.answered}</span>
              </p>
              <p className="flex items-center gap-1.5 text-ink-2">
                <span className="h-2 w-2 rounded-sm" style={{ background: 'var(--color-chart-2)' }} /> Abandoned <span className="tabular ml-auto text-ink">{hd.abandoned}</span>
              </p>
              <p className="mt-1 text-ink-3">
                {hd.offered} calls{hd.offered > hd.answered + hd.abandoned ? `, ${hd.offered - hd.answered - hd.abandoned} other (voicemail, closed)` : ''}
                {hd.wait !== null ? `, average wait ${duration(hd.wait)}` : ''}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
